import React, { FormEvent, useState } from 'react';

import { Money } from '@betterment/core';
import { useAutoNavigate, useMutation } from '@betterment/js-runtime';
import { Banner, Button, Dialog, Text } from '@betterment/samba-react';
import * as Sentry from '@sentry/browser';

import { urlBuilder } from '../../../../../Shared/urlBuilder';

interface UnfundedCloseAccountModalDescriptionInterface {
  name: string;
  purposeName?: string;
  legacyAccountId: number;
}
const UnfundedCloseAccountModalDescription: React.FC<UnfundedCloseAccountModalDescriptionInterface> = ({
  name,
  purposeName,
  legacyAccountId,
}) => {
  const formPath = `/app/sub_accounts/${legacyAccountId}/removal_confirmation`;
  const [confirmDelete] = useMutation<{ responseURL: string }>(formPath, {
    qs: { returnTo: '/' },
    method: 'post',
  });

  const [deletionErrors, setDeletionErrors] = useState<string[]>();
  const [isDeletionPending, setIsDeletionPending] = useState(false);

  const autoNavigate = useAutoNavigate();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsDeletionPending(true);
    const res = await confirmDelete();

    if (res.errors) {
      const wrappedError = new Error('account_closure_error', { cause: res.errors });
      Sentry.captureException(wrappedError);
      setDeletionErrors(Object.values(res.errors.messages).flat());
      setIsDeletionPending(false);
      return;
    }

    void autoNavigate(urlBuilder.appHome.absolute());
  };

  const description = purposeName
    ? `This action can’t be undone. Closing your ${name} account will close your ${purposeName} goal and cancel any associated recurring deposits, withdrawals, or transfers.`
    : `This action can’t be undone. Closing your ${name} account will cancel any associated recurring deposits, withdrawals, or transfers.`;
  return (
    <>
      <Dialog.Description asChild={true}>
        <Text as="p" css={{ mb: 'space.150' }}>
          {description}
        </Text>
      </Dialog.Description>
      {deletionErrors && (
        <Banner variant="critical" css={{ mb: 'space.150' }}>
          <Banner.Content>
            {deletionErrors.map(error => (
              <Text as="p" key={error}>
                {error}
              </Text>
            ))}
          </Banner.Content>
        </Banner>
      )}
      <form onSubmit={handleSubmit} data-testid="close-account-form" style={{ display: 'inline' }}>
        <Button type="submit" disabled={!!deletionErrors} loading={isDeletionPending}>
          Close account
        </Button>
      </form>
      <Dialog.Close asChild={true}>
        <Button variant="ghostPrimary">Cancel</Button>
      </Dialog.Close>
    </>
  );
};

const FundedCloseAccountModalDescription: React.FC = () => {
  return (
    <>
      <Dialog.Description asChild={true}>
        <Text as="p">You’ll need to transfer or withdraw all remaining funds before your account can be closed.</Text>
      </Dialog.Description>
      <Dialog.Close>Got it</Dialog.Close>
    </>
  );
};

type CloseAccountModalProps = {
  balance: Money;
  purposeName?: string;
  name: string;
  legacyAccountId: number;
  triggerText: string;
};

export const CloseAccountModal: React.FC<CloseAccountModalProps> = ({
  purposeName,
  name,
  balance,
  legacyAccountId,
  triggerText,
}) => {
  const isFunded = !!balance && balance.cents > 0;

  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <Button variant="textCritical" css={{ margin: '0 auto' }}>
          {triggerText}
        </Button>
      </Dialog.Trigger>
      <Dialog.Content size="medium" css={{ p: 'space.400' }}>
        <Dialog.Title>{isFunded ? 'Close your account' : 'Are you sure you want to close this account?'}</Dialog.Title>
        {isFunded ? (
          <FundedCloseAccountModalDescription />
        ) : (
          <UnfundedCloseAccountModalDescription
            name={name}
            purposeName={purposeName}
            legacyAccountId={legacyAccountId}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
};
